import { Grid, makeStyles } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useFormState, useForm } from 'react-final-form';
import _ from 'lodash';

import { fetchTributosTtceByNcmPais } from '../../../features/ncm-subitem/ncmSubitemAPI';

import QCXSelectManyFundamentosOpcionais from './components/QCXSelectManyFundamentosOpcionais';
import QCXSelectManyFundamentosObrigatorios from './components/QCXSelectManyFundamentosObrigatorios';
import QCXAtributosTratamentoTributarioDuimp from '../../../shared-components/atributos-tratamento-tributario-duimp/QCXAtributosTratamentoTributarioDuimp';
import QCXAtributosFundamentoLegalDuimpFormGroup from '../../../shared-components/atributos-fundamento-legal-duimp-form-group/QCXAtributosFundamentoLegalDuimpFormGroup';

import QCXFormSubtitle from '../../form-title/QCXFormSubtitle';
import QCXFormTitle from '../../form-title/QCXFormTitle';

export default function TributosAdicaoDuimpForm({ values, disabled, idFatura }) {
  const form = useForm();

  const [idNcm, setIdNcm] = useState(null);
  const [idPais, setIdPais] = useState(null);
  const [tributos, setTributos] = useState([]);
  const [tributosLoading, setTributosLoading] = useState(false);

  const [fundamentosObrigatorios, setFundamentosObrigatorios] = useState([]);
  const [fundamentosOpcionaisGrouped, setFundamentosOpcionaisGrouped] = useState([]);

  const obterIdPaisAdicao = (adicao) => {
    let pais = null;
    const relacao = _.get(adicao, 'relacao', null);
    const exportadorPais =
      _.get(adicao, 'exportador.endereco.cidade.estado.pais.id', null) ??
      _.get(adicao, 'exportador.endereco.estado.pais.id', null) ??
      _.get(adicao, 'exportador.endereco.pais.id', null);
    const fabricantePais =
      _.get(adicao, 'fabricante.endereco.cidade.estado.pais.id', null) ??
      _.get(adicao, 'fabricante.endereco.estado.pais.id', null) ??
      _.get(adicao, 'fabricante.endereco.pais.id', null);

    if (relacao === 'FABRICANTE_EXPORTADOR') {
      pais = exportadorPais;
    } else if (relacao === 'FABRICANTE_NAO_EXPORTADOR') {
      pais = fabricantePais;
    } else if (relacao === 'FABRICANTE_DESCONHECIDO') {
      pais = _.get(adicao, 'paisOrigem.id', null) ?? _.get(adicao, 'mercadorias[0].paisOrigem.id', null);
    }

    return pais;
  };

  useEffect(() => {
    const ncm = values?.ncm?.id;
    const pais = obterIdPaisAdicao(values);

    if (ncm !== idNcm) {
      setIdNcm(ncm);
    }

    if (pais !== idPais) {
      setIdPais(pais);
    }
  }, [values]);

  const normalizeMetadados = (codTratamento, mercadorias) => {
    if (!mercadorias || !Array.isArray(mercadorias)) {
      return { atributosUnicos: {}, combinacoesValidas: [] };
    }

    const mapperAtributos = {};
    const combinacoesValidas = [];

    mercadorias.forEach((mercadoria) => {
      const atributos = mercadoria?.atributos || [];

      const combinacao = atributos.map((attr) => ({
        codigo: attr?.codigo,
        valor: attr?.valor,
      }));

      combinacoesValidas.push(combinacao);

      atributos.forEach((atributo) => {
        const codigo = atributo?.codigo || '';

        if (mapperAtributos[codigo]) {
          if (atributo?.tipoCodigo === 'Domínio dinâmico') {
            const jaExiste = mapperAtributos[codigo].dominio.some((d) => d.valor === atributo?.valor);

            if (!jaExiste) {
              mapperAtributos[codigo].dominio.push({
                valor: atributo?.valor,
                descricao: atributo?.descricaoValor,
              });
            }
          }
        } else {
          const newAtributo = {
            codigo: atributo?.codigo || '',
            descricaoCodigo: atributo?.descricaoCodigo || '',
            tipoCodigo: atributo?.tipoCodigo || '',
            codTratamento,
            dominio: [],
          };

          if (atributo?.tipoCodigo === 'Domínio dinâmico') {
            newAtributo.dominio.push({
              valor: atributo?.valor,
              descricao: atributo?.descricaoValor,
            });
          }

          mapperAtributos[codigo] = newAtributo;
        }
      });
    });

    return {
      atributosUnicos: mapperAtributos,
      combinacoesValidas,
    };
  };

  const agruparFundamentos = (fundamentosMetadadosArr) => {
    if (!fundamentosMetadadosArr) return [];

    const fundamentos = {};

    fundamentosMetadadosArr.forEach((item) => {
      const codigoFundamento = item.fundamentoLegal.codigo;
      const nomeFundamento = item.fundamentoLegal.nome;
      const tipoFundamento = item.fundamentoLegal.tipo;

      if (!fundamentos[codigoFundamento]) {
        fundamentos[codigoFundamento] = {
          id: codigoFundamento,
          nome: nomeFundamento,
          tipo: tipoFundamento,
          tratamentos: [],
        };
      }

      fundamentos[codigoFundamento].tratamentos.push({
        regime: {
          codigo: item?.regime?.codigo,
          nome: item?.regime?.nome,
        },
        tributo: {
          codigo: item?.tributo?.codigo,
          nome: item?.tributo?.nome,
        },
      });
    });

    const fundamentosParsed = Object.values(fundamentos).map((fundamento) => ({ ...fundamento }));
    const fundamentosOrdenados = fundamentosParsed.sort((a, b) => a.id.localeCompare(b.id));

    return fundamentosOrdenados;
  };

  useEffect(() => {
    if (idNcm && idPais) {
      setTributosLoading(true);

      fetchTributosTtceByNcmPais(idNcm, idPais)
        .then((response) => {
          const responseData = response?.data || {};
          const tratamentosObrigatorios = _.isArray(responseData?.tratamentosTributarios)
            ? responseData.tratamentosTributarios
            : [];
          const tratamentosOpcionaisDisponiveis = _.isArray(responseData?.fundamentosOpcionaisDisponiveis)
            ? responseData.fundamentosOpcionaisDisponiveis
            : [];

          if (tratamentosOpcionaisDisponiveis.length) {
            const fundamentosOrdenados = agruparFundamentos(tratamentosOpcionaisDisponiveis);
            setFundamentosOpcionaisGrouped(fundamentosOrdenados);
          }

          if (tratamentosObrigatorios.length) {
            // agrupa fundamentos obrigatorios para exibicao
            const fundamentosOrdenados = agruparFundamentos(tratamentosObrigatorios);
            setFundamentosObrigatorios(fundamentosOrdenados);
          }

          const tratamentosParaMetadados = [...tratamentosObrigatorios, ...tratamentosOpcionaisDisponiveis];

          if (tratamentosParaMetadados.length) {
            // normaliza metadados de campos relacionados a tratamentos tributarios
            const atributosValores = {};
            const atributosNormalizados = [];
            const todasCombinacoesValidas = {};

            tratamentosParaMetadados.forEach((tratamento) => {
              const codTratamento = `${tratamento?.tributo?.codigo};${tratamento?.regime?.codigo};${tratamento?.fundamentoLegal?.codigo}`;

              const { atributosUnicos: atributosObj, combinacoesValidas } = normalizeMetadados(
                codTratamento,
                tratamento.mercadorias
              );

              todasCombinacoesValidas[codTratamento] = combinacoesValidas;
              const valoresPreenchidos = _.get(values, 'valoresMetadadoTtce.valorMetadadosJson', {});

              const valoresFields = {};
              const gruposPreenchidos = ['obrigatorios', 'opcionais'];

              gruposPreenchidos.forEach((grupo) => {
                const grupoValores = valoresPreenchidos?.[grupo];
                console.log('grupo valores', grupoValores);
                if (!grupoValores) return;

                Object.entries(grupoValores).forEach(([key, value]) => {
                  if (value?.atributos && _.isArray(value?.atributos)) {
                    value.atributos.forEach((atributo) => {
                      valoresFields[`${atributo.codigo}`] = atributo.valor;
                    });
                  }
                });
              });

              Object.entries(atributosObj).forEach(([key, value]) => {
                const hasValor = Object.prototype.hasOwnProperty.call(valoresFields, key);
                atributosValores[key] = { ...value, valor: hasValor ? valoresFields[key] : '' };
                atributosNormalizados.push(value);
              });
            });

            form.change(
              'ignorableFields.tratamentosTtce.metadados.valores',
              atributosValores // objeto react-final-form
            );

            responseData.metadadosNormalizados = atributosNormalizados;
            responseData.combinacoesValidas = todasCombinacoesValidas;
          }

          console.log(responseData);

          setTributos(responseData || []);
        })
        .catch((error) => {
          console.error(error);
          setTributos([]);
        })
        .finally(() => {
          setTributosLoading(false);
        });
    }
  }, [idNcm, idPais]);

  const modes = useMemo(
    () => ({
      isConsult: disabled,
    }),
    [disabled]
  );

  const obterCodigoTratamento = useCallback((tratamento) => {
    const fundamentoCodigo =
      tratamento?.fundamentoLegal?.codigo ?? tratamento?.fundamento?.id ?? tratamento?.fundamento?.codigo;
    return `${tratamento?.tributo?.codigo};${tratamento?.regime?.codigo};${fundamentoCodigo}`;
  }, []);

  const tributosFiltrados = useMemo(() => {
    const metadadosNormalizados = tributos?.metadadosNormalizados || [];
    const combinacoesValidas = tributos?.combinacoesValidas || {};
    const tratamentosObrigatorios = tributos?.tratamentosTributarios || [];
    const tratamentosOpcionaisDisponiveis = tributos?.fundamentosOpcionaisDisponiveis || [];
    const tratamentosOpcionaisSelecionados = _.get(values, 'ignorableFields.tratamentosTtce.metadados.opcionais', []);

    const codigosObrigatorios = new Set(tratamentosObrigatorios.map(obterCodigoTratamento));
    const codigosOpcionaisDisponiveis = new Set(tratamentosOpcionaisDisponiveis.map(obterCodigoTratamento));
    const codigosOpcionaisSelecionados = new Set(tratamentosOpcionaisSelecionados.map(obterCodigoTratamento));

    const metadadosPermitidos = metadadosNormalizados.filter((metadado) => {
      const codTratamento = metadado?.codTratamento;
      if (codigosObrigatorios.has(codTratamento)) return true;
      if (codigosOpcionaisDisponiveis.has(codTratamento)) return codigosOpcionaisSelecionados.has(codTratamento);
      return true;
    });

    const metadadosFiltrados = metadadosPermitidos.reduce((acc, metadado) => {
      const codigo = metadado?.codigo;
      if (!codigo) return acc;
      if (!acc.find((item) => item.codigo === codigo)) {
        acc.push(metadado);
      }
      return acc;
    }, []);

    const codigosPermitidos = new Set(metadadosFiltrados.map((metadado) => metadado?.codTratamento));
    const combinacoesFiltradas = Object.keys(combinacoesValidas).reduce((acc, cod) => {
      if (codigosPermitidos.has(cod)) {
        acc[cod] = combinacoesValidas[cod];
      }
      return acc;
    }, {});

    return {
      ...tributos,
      metadadosNormalizados: metadadosFiltrados,
      combinacoesValidas: combinacoesFiltradas,
    };
  }, [tributos, values, obterCodigoTratamento]);

  return (
    <>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <QCXFormSubtitle title="Tratamentos Opcionais" />
      </Grid>

      <QCXSelectManyFundamentosOpcionais
        title="Fundamentos Opcionais"
        name="ignorableFields.tratamentosTtce.metadados.opcionais"
        values={values}
        disabled={disabled}
        configureFilterPredicate={(item) => true}
        fundamentosOpcionaisGrouped={fundamentosOpcionaisGrouped}
        idFatura={idFatura}
      />

      <Grid item xs={12} sm={12} md={12} lg={12}>
        <QCXFormSubtitle title="Tratamentos Obrigatorios" />
      </Grid>

      <QCXSelectManyFundamentosObrigatorios
        title="Fundamentos Obrigatorios"
        name="ignorableFields.tratamentosTtce.metadados.obrigatorios"
        values={values}
        disabled={disabled}
        configureFilterPredicate={(item) => true}
        fundamentosGrouped={fundamentosObrigatorios}
      />

      <>
        <QCXAtributosTratamentoTributarioDuimp
          name="ignorableFields.tratamentosTtce.metadados.valores"
          modes={modes}
          metadados={tributosFiltrados}
        />
      </>

      <>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <QCXFormTitle title={'Atributos de Fundamento Legal DUIMP'.toUpperCase()} />
        </Grid>
        <QCXAtributosFundamentoLegalDuimpFormGroup modes={modes} />
      </>
    </>
  );
}
